import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { getAgentStats, type DayBucket } from './api';

const RANGES: { label: string; days: number }[] = [
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
];

export function ActivityDashboard({ orgId, agentId }: { orgId: string; agentId: string }) {
  const [days, setDays] = useState(14);
  const stats = useQuery({
    queryKey: ['agent-stats', orgId, agentId, days],
    queryFn: () => getAgentStats(orgId, agentId, days),
    enabled: !!orgId && !!agentId,
    refetchInterval: 30_000,
  });

  return (
    <Card>
      <CardHeader
        title="Activity"
        description="Daily runs, token spend and on-chain transactions. Refreshes every 30 seconds."
      />
      <CardBody className="space-y-4">
        <div className="flex items-center justify-between">
          <QuotaBar
            today={stats.data?.runs_today ?? 0}
            cap={stats.data?.max_runs_per_day ?? 0}
          />
          <RangeSwitcher value={days} onChange={setDays} />
        </div>

        {stats.isLoading && <p className="text-sm text-ink-tertiary">Loading…</p>}
        {stats.data && (
          <div className="space-y-5">
            <Series
              label="Runs"
              total={sum(stats.data.buckets, (b) => b.runs_total)}
              buckets={stats.data.buckets}
              breakdown={[
                { key: 'runs_done', label: 'done', tone: 'bg-ok' },
                { key: 'runs_failed', label: 'failed', tone: 'bg-danger' },
                { key: 'runs_timeout', label: 'timeout', tone: 'bg-amber-400' },
              ]}
            />
            <Series
              label="Transactions"
              total={sum(stats.data.buckets, (b) => b.tx_total)}
              buckets={stats.data.buckets}
              breakdown={[
                { key: 'tx_confirmed', label: 'confirmed', tone: 'bg-ok' },
                { key: 'tx_broadcast', label: 'broadcast', tone: 'bg-bit2me' },
                { key: 'tx_reverted', label: 'reverted', tone: 'bg-danger' },
                { key: 'tx_timeout', label: 'timeout', tone: 'bg-amber-400' },
                { key: 'tx_failed', label: 'failed', tone: 'bg-danger/70' },
              ]}
            />
            <TokenSpend buckets={stats.data.buckets} />
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function QuotaBar({ today, cap }: { today: number; cap: number }) {
  const pct = cap > 0 ? Math.min(100, Math.round((today / cap) * 100)) : 0;
  const tone =
    pct >= 90 ? 'bg-danger' : pct >= 70 ? 'bg-amber-400' : 'bg-bit2me';
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">
        Today
      </p>
      <div className="mt-1 flex items-center gap-2">
        <div className="h-1.5 w-32 overflow-hidden rounded-full bg-surface-soft">
          <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
        </div>
        <p className="font-mono text-xs text-ink-secondary">
          {today} / {cap || '∞'} runs
        </p>
      </div>
    </div>
  );
}

function RangeSwitcher({ value, onChange }: { value: number; onChange: (d: number) => void }) {
  return (
    <div className="inline-flex rounded-lg border border-line-soft bg-surface-white p-0.5">
      {RANGES.map((r) => (
        <button
          key={r.days}
          type="button"
          onClick={() => onChange(r.days)}
          className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
            value === r.days
              ? 'bg-bit2me text-white'
              : 'text-ink-secondary hover:bg-surface-soft hover:text-ink-primary'
          }`}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

type Slice = { key: keyof DayBucket; label: string; tone: string };

function Series({
  label,
  total,
  buckets,
  breakdown,
}: {
  label: string;
  total: number;
  buckets: DayBucket[];
  breakdown: Slice[];
}) {
  // We don't know which "total" to scale by per series, so we accept either
  // a runs-series or tx-series via the breakdown keys' prefixes.
  const isRuns = breakdown[0].key.startsWith('runs_');
  const trueMax = useMemo(
    () => Math.max(1, ...buckets.map((b) => (isRuns ? b.runs_total : b.tx_total))),
    [buckets, isRuns],
  );

  return (
    <div>
      <div className="mb-2 flex items-end justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">
            {label}
          </p>
          <p className="text-xl font-semibold text-ink-primary">{total}</p>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {breakdown.map((s) => (
            <span key={s.label} className="flex items-center gap-1 text-[11px] text-ink-tertiary">
              <span className={`h-2 w-2 rounded-sm ${s.tone}`} />
              {s.label}
            </span>
          ))}
        </div>
      </div>
      <div className="flex h-24 items-end gap-1">
        {buckets.map((b, i) => {
          const dayTotal = isRuns ? b.runs_total : b.tx_total;
          const height = (dayTotal / trueMax) * 100;
          return (
            <div
              key={i}
              className="group relative flex-1"
              title={`${formatDay(b.day)} · ${dayTotal} ${isRuns ? 'runs' : 'tx'}`}
            >
              {dayTotal > 0 ? (
                <StackedBar bucket={b} breakdown={breakdown} totalHeight={height} />
              ) : (
                <div className="h-[2px] w-full rounded-sm bg-surface-soft" />
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between font-mono text-[10px] text-ink-tertiary">
        <span>{formatDay(buckets[0]?.day)}</span>
        <span>{formatDay(buckets[buckets.length - 1]?.day)}</span>
      </div>
    </div>
  );
}

function StackedBar({
  bucket,
  breakdown,
  totalHeight,
}: {
  bucket: DayBucket;
  breakdown: Slice[];
  totalHeight: number;
}) {
  const total = breakdown.reduce((s, slice) => s + Number(bucket[slice.key] || 0), 0) || 1;
  return (
    <div
      className="mx-auto flex w-3/4 flex-col-reverse overflow-hidden rounded-sm"
      style={{ height: `${totalHeight}%`, minHeight: '4px' }}
    >
      {breakdown.map((s) => {
        const v = Number(bucket[s.key] || 0);
        if (!v) return null;
        return <div key={s.label} className={s.tone} style={{ height: `${(v / total) * 100}%` }} />;
      })}
    </div>
  );
}

function TokenSpend({ buckets }: { buckets: DayBucket[] }) {
  const inTotal = buckets.reduce((s, b) => s + Number(b.tokens_input || 0), 0);
  const outTotal = buckets.reduce((s, b) => s + Number(b.tokens_output || 0), 0);
  const max = useMemo(
    () =>
      Math.max(1, ...buckets.map((b) => Number(b.tokens_input || 0) + Number(b.tokens_output || 0))),
    [buckets],
  );

  return (
    <div>
      <div className="mb-2 flex items-end justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">
            LLM tokens
          </p>
          <p className="text-xl font-semibold text-ink-primary">
            {fmtCompact(inTotal + outTotal)}
            <span className="ml-2 font-mono text-xs text-ink-tertiary">
              {fmtCompact(inTotal)} in · {fmtCompact(outTotal)} out
            </span>
          </p>
        </div>
      </div>
      <div className="flex h-16 items-end gap-1">
        {buckets.map((b, i) => {
          const tIn = Number(b.tokens_input || 0);
          const tOut = Number(b.tokens_output || 0);
          const dayTotal = tIn + tOut;
          const height = (dayTotal / max) * 100;
          if (!dayTotal) {
            return <div key={i} className="h-[2px] flex-1 rounded-sm bg-surface-soft" />;
          }
          return (
            <div
              key={i}
              className="group relative flex-1"
              title={`${formatDay(b.day)} · ${fmtCompact(tIn)} in / ${fmtCompact(tOut)} out`}
            >
              <div
                className="mx-auto flex w-3/4 flex-col-reverse overflow-hidden rounded-sm"
                style={{ height: `${height}%`, minHeight: '4px' }}
              >
                {tIn > 0 && (
                  <div
                    className="bg-bit2me"
                    style={{ height: `${(tIn / dayTotal) * 100}%` }}
                  />
                )}
                {tOut > 0 && (
                  <div
                    className="bg-bit2me/40"
                    style={{ height: `${(tOut / dayTotal) * 100}%` }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function sum(buckets: DayBucket[], get: (b: DayBucket) => number): number {
  return buckets.reduce((s, b) => s + Number(get(b) || 0), 0);
}

function formatDay(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}

function fmtCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}
